import React from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useGetMarketplacePlans } from "../../client/hooks/marketplace/marketplace.queries";
import {
  parseAllowedCategories,
  MENU_CATEGORIES,
  SUBMENU_CATEGORIES,
  isValidPlan,
} from "../../utils/menuAccess";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("pt-BR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(amount);
};

const getBillingCycleText = (cycle) => {
  const cycles = {
    monthly: "Mensal",
    yearly: "Anual",
    quarterly: "Trimestral",
  };
  return cycles[cycle] || cycle;
};

const getAccessibleFeatures = (allowedCategories, hasFullAccess) => {
  const accessibleMenus = [];
  const accessibleSubmenus = [];

  Object.entries(MENU_CATEGORIES).forEach(([index, menuName]) => {
    const menuIndex = parseInt(index);
    let hasMenuAccess = false;

    if (hasFullAccess) {
      hasMenuAccess = true;
    } else {
      hasMenuAccess = allowedCategories.some(
        (allowed) => allowed.parent === menuName.toLowerCase()
      );
    }

    if (hasMenuAccess) {
      accessibleMenus.push(menuName);
    }

    if (SUBMENU_CATEGORIES[menuIndex]) {
      Object.entries(SUBMENU_CATEGORIES[menuIndex]).forEach(
        ([, submenuPath]) => {
          const [parentCat, childCat] = submenuPath.split("/");

          let hasSubmenuAccess = false;
          if (hasFullAccess) {
            hasSubmenuAccess = true;
          } else {
            hasSubmenuAccess = allowedCategories.some((allowed) => {
              return (
                allowed.parent === parentCat.toLowerCase().replace(/-/g, " ") &&
                allowed.child === childCat.toLowerCase().replace(/-/g, " ")
              );
            });

            if (!hasSubmenuAccess) {
              hasSubmenuAccess = allowedCategories.some(
                (allowed) =>
                  allowed.parent ===
                    parentCat.toLowerCase().replace(/-/g, " ") &&
                  allowed.child === null
              );
            }
          }

          if (hasSubmenuAccess) {
            accessibleSubmenus.push(
              submenuPath.replace(/-/g, " ").replace("/", " > ")
            );
          }
        }
      );
    }
  });

  return { accessibleMenus, accessibleSubmenus };
};

const LoadingState = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="400px"
  >
    <CircularProgress />
  </Box>
);

const ErrorState = ({ message }) => (
  <Box p={3}>
    <Typography color="error">
      Erro ao carregar informações do plano: {message}
    </Typography>
  </Box>
);

const ContactCard = () => {
  const handleWhatsAppClick = () => {
    const phone = "5551997971635";
    const message = "Olá! Preciso renovar meu plano no Wala Smart Work.";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
      p={3}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          border: "1px solid rgba(255, 112, 67, 0.1)",
          textAlign: "center",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box mb={3}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: "rgba(255, 152, 0, 0.1)",
                color: "#ff9800",
                mx: "auto",
                mb: 2,
              }}
            >
              <WarningIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography
              variant="h5"
              fontWeight={600}
              mb={1}
              sx={{ color: "#2c3e50" }}
            >
              Plano Expirado
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              Seu plano atual está expirado ou não foi encontrado. Entre em
              contato conosco para renovar seu plano e continuar aproveitando
              todas as funcionalidades.
            </Typography>
          </Box>

          <Box mb={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={handleWhatsAppClick}
              sx={{
                background: "linear-gradient(135deg, #25D366 0%, #1BAF4F 100%)",
                color: "white",
                fontWeight: 600,
                fontSize: "1.1rem",
                py: 1.5,
                px: 3,
                borderRadius: 2,
                mb: 2,
                width: "100%",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1BAF4F 0%, #128C3F 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(37, 211, 102, 0.4)",
                },
              }}
            >
              Entrar em Contato via WhatsApp
            </Button>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Ou entre em contato através de:
            </Typography>

            <Box display="flex" justifyContent="center" gap={2}>
              <Chip
                icon={<PhoneIcon />}
                label="(51) 9 9797-1635"
                clickable
                onClick={() => window.open("tel:+5551997971635")}
                sx={{
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                  color: "#1976d2",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.15)",
                  },
                }}
              />
              <Chip
                icon={<EmailIcon />}
                label="jonas@walaplace.com.br"
                clickable
                onClick={() => window.open("mailto:jonas@walaplace.com.br")}
                sx={{
                  bgcolor: "rgba(255, 112, 67, 0.08)",
                  color: "#FF7322",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "rgba(255, 112, 67, 0.15)",
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            p={2}
            sx={{
              bgcolor: "rgba(37, 211, 102, 0.08)",
              borderRadius: 2,
              border: "1px solid rgba(37, 211, 102, 0.2)",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#1BAF4F", fontWeight: 600 }}
            >
              ⚡ Resposta rápida no WhatsApp
            </Typography>
            <Typography variant="body2" sx={{ color: "#1BAF4F", mt: 0.5 }}>
              Nossa equipe responde em até 2 horas durante o horário comercial
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const NoActivePlanState = () => <ContactCard />;

const PlanHeader = ({ plan }) => (
  <Paper
    sx={{
      p: 3,
      mb: 4,
      background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
      boxShadow: "0 4px 12px rgba(255, 112, 67, 0.08)",
      borderRadius: 2,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "4px",
        background:
          "linear-gradient(90deg, #FF7322 0%, #ff9800 50%, #4caf50 100%)",
      }}
    />

    <Typography variant="h4" fontWeight={600} mb={2} sx={{ color: "#2c3e50" }}>
      Plano Atual
    </Typography>

    <Box display="flex" alignItems="center" mb={2}>
      <Typography variant="h5" fontWeight={600} sx={{ color: "#FF7322" }}>
        {plan.name}
      </Typography>
      <Chip
        label="Ativo"
        sx={{
          ml: 2,
          bgcolor: "rgba(46, 125, 50, 0.1)",
          color: "#2e7d32",
          fontWeight: 600,
          boxShadow: "0 2px 6px rgba(46, 125, 50, 0.2)",
        }}
      />
    </Box>

    {plan.description && (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ lineHeight: 1.6 }}
      >
        {plan.description}
      </Typography>
    )}
  </Paper>
);

const PlanMainInfo = ({ plan }) => (
  <Paper
    sx={{
      p: 3,
      mb: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      borderRadius: 2,
      border: "1px solid rgba(255, 112, 67, 0.08)",
    }}
  >
    <Typography variant="h6" fontWeight={600} mb={3} sx={{ color: "#2c3e50" }}>
      Informações do Plano
    </Typography>

    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Preço
        </Typography>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 1,
            bgcolor: "rgba(255, 112, 67, 0.08)",
          }}
        >
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ color: "#FF7322" }}
          >
            {formatCurrency(plan.price, plan.currency)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Ciclo de Cobrança
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {getBillingCycleText(plan.billing_cycle)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Data de Expiração
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {formatDate(plan.expire_in)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Renovação Automática
        </Typography>
        <Chip
          label={plan.auto_renewal ? "Ativada" : "Desativada"}
          size="small"
          sx={{
            bgcolor: plan.auto_renewal
              ? "rgba(46, 125, 50, 0.1)"
              : "rgba(158, 158, 158, 0.1)",
            color: plan.auto_renewal ? "#2e7d32" : "#666",
            fontWeight: 500,
          }}
        />
      </Box>
    </Box>
  </Paper>
);

const PlanLimitsInfo = ({ plan }) => (
  <Paper
    sx={{
      p: 3,
      mb: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      borderRadius: 2,
      border: "1px solid rgba(255, 112, 67, 0.08)",
    }}
  >
    <Typography variant="h6" fontWeight={600} mb={3} sx={{ color: "#2c3e50" }}>
      Limites e Configurações
    </Typography>

    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          ID do Plano
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {plan.id}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Tipo do Plano
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {plan.plan_type}
        </Typography>
      </Box>

      {plan.max_listings && (
        <>
          <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Máximo de Anúncios
            </Typography>
            <Chip
              label={plan.max_listings}
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: "#1976d2",
                fontWeight: 500,
              }}
            />
          </Box>
        </>
      )}

      {plan.max_categories && (
        <>
          <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Máximo de Categorias
            </Typography>
            <Chip
              label={plan.max_categories}
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: "#1976d2",
                fontWeight: 500,
              }}
            />
          </Box>
        </>
      )}

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Anúncios em Destaque
        </Typography>
        <Chip
          label={plan.featured_listings_included || 0}
          size="small"
          sx={{
            bgcolor: "rgba(156, 39, 176, 0.08)",
            color: "#9c27b0",
            fontWeight: 500,
          }}
        />
      </Box>

      {plan.commission_rate && (
        <>
          <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Taxa de Comissão
            </Typography>
            <Chip
              label={`${plan.commission_rate}%`}
              size="small"
              sx={{
                bgcolor: "rgba(255, 112, 67, 0.08)",
                color: "#FF7322",
                fontWeight: 500,
              }}
            />
          </Box>
        </>
      )}

      {plan.trial_period_days > 0 && (
        <>
          <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Período de Teste
            </Typography>
            <Chip
              label={`${plan.trial_period_days} dias`}
              size="small"
              sx={{
                bgcolor: "rgba(76, 175, 80, 0.08)",
                color: "#4caf50",
                fontWeight: 500,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  </Paper>
);

const AccessFeatures = ({
  accessibleMenus,
  accessibleSubmenus,
  hasFullAccess,
}) => (
  <Paper
    sx={{
      p: 3,
      mb: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      borderRadius: 2,
      border: "1px solid rgba(255, 112, 67, 0.08)",
    }}
  >
    <Typography variant="h6" fontWeight={600} mb={3} sx={{ color: "#2c3e50" }}>
      Funcionalidades Disponíveis
    </Typography>

    {hasFullAccess && (
      <Box
        mb={3}
        p={2.5}
        sx={{
          bgcolor: "rgba(46, 125, 50, 0.08)",
          borderRadius: 2,
          border: "1px solid rgba(46, 125, 50, 0.2)",
          position: "relative",
        }}
      >
        <Typography variant="body1" sx={{ color: "#2e7d32", fontWeight: 600 }}>
          ✓ Acesso Completo
        </Typography>
        <Typography variant="body2" sx={{ color: "#2e7d32", mt: 0.5 }}>
          Todas as funcionalidades estão disponíveis
        </Typography>
      </Box>
    )}

    <Box mb={3}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={2}
        sx={{ color: "#FF7322" }}
      >
        Menus Principais
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {accessibleMenus.map((menu, index) => (
          <Chip
            key={index}
            label={menu}
            sx={{
              bgcolor: "rgba(255, 112, 67, 0.08)",
              color: "#FF7322",
              fontWeight: 500,
              "&:hover": {
                bgcolor: "rgba(255, 112, 67, 0.12)",
              },
            }}
          />
        ))}
      </Box>
    </Box>

    {accessibleSubmenus.length > 0 && (
      <Box>
        <Divider sx={{ mb: 3, borderColor: "rgba(255, 112, 67, 0.1)" }} />
        <Typography
          variant="subtitle1"
          fontWeight={600}
          mb={2}
          sx={{ color: "#1976d2" }}
        >
          Funcionalidades Específicas
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {accessibleSubmenus.map((submenu, index) => (
            <Chip
              key={index}
              label={submenu}
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: "#1976d2",
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "rgba(25, 118, 210, 0.12)",
                },
              }}
            />
          ))}
        </Box>
      </Box>
    )}
  </Paper>
);

const PlanDates = ({ plan }) => (
  <Paper
    sx={{
      p: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      borderRadius: 2,
      border: "1px solid rgba(255, 112, 67, 0.08)",
      height: "fit-content",
    }}
  >
    <Typography variant="h6" fontWeight={600} mb={3} sx={{ color: "#2c3e50" }}>
      Histórico
    </Typography>

    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          mb={0.5}
        >
          Data de Criação
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {formatDate(plan.createdAt)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 112, 67, 0.1)" }} />

      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          mb={0.5}
        >
          Última Atualização
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {formatDate(plan.updatedAt)}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const PlanoAtualPage = () => {
  const {
    data: marketplacePlans = [],
    isLoading,
    error,
  } = useGetMarketplacePlans();

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;

  if (!isValidPlan(marketplacePlans)) {
    return <NoActivePlanState />;
  }

  const activePlan = marketplacePlans.find((plan) => plan.status === 1);
  const allowedCategories = parseAllowedCategories(activePlan.categories);
  const hasFullAccess = allowedCategories.includes("ALL");
  const { accessibleMenus, accessibleSubmenus } = getAccessibleFeatures(
    allowedCategories,
    hasFullAccess
  );

  return (
    <Box
      p={3}
      sx={{
        bgcolor: "#fafafa",
        minHeight: "100vh",
      }}
    >
      <PlanHeader plan={activePlan} />

      <Box display="flex" gap={3} flexWrap={{ xs: "wrap", lg: "nowrap" }}>
        <Box flex={2} display="flex" flexDirection="column" minWidth={0}>
          <PlanMainInfo plan={activePlan} />
          <PlanLimitsInfo plan={activePlan} />
          <AccessFeatures
            accessibleMenus={accessibleMenus}
            accessibleSubmenus={accessibleSubmenus}
            hasFullAccess={hasFullAccess}
          />
        </Box>

        <Box flex={1} sx={{ minWidth: { xs: "100%", lg: "300px" } }}>
          <PlanDates plan={activePlan} />
        </Box>
      </Box>
    </Box>
  );
};

export default PlanoAtualPage;
